import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon, CurrencyDollarIcon, AdjustmentsHorizontalIcon,
  ShoppingBagIcon, ShoppingCartIcon, TruckIcon, PhoneIcon, FilmIcon, WifiIcon, GiftIcon
} from '@heroicons/react/24/outline';
import { formatCurrencyInput, parseCurrencyInput } from '../utils/currencyUtils';
const categories = [
  { id: 'food', label: 'Makan', icon: ShoppingBagIcon },
  { id: 'transport', label: 'Transport', icon: TruckIcon },
  { id: 'groceries', label: 'Belanja', icon: ShoppingCartIcon },
  { id: 'entertainment', label: 'Hiburan', icon: FilmIcon },
  { id: 'internet', label: 'Internet', icon: WifiIcon },
  { id: 'other', label: 'Lainnya', icon: GiftIcon }
];

const EditBillModal = ({ transaction, members, onClose, onSubmit, onDelete }) => {
  const [formData, setFormData] = useState({
    description: transaction.description,
    amount: String(transaction.amount),
    category: transaction.category,
    paid_by_member_id: transaction.paidByMemberId || members[0]?.id || '',
  });
  const initialSelectedMembers = members.filter(m => transaction.splitBetween.includes(m.name)).map(m => m.id);
  const [selectedMembers, setSelectedMembers] = useState(initialSelectedMembers);

  const isCustom = (() => {
    if (!transaction.splitAmounts || Object.keys(transaction.splitAmounts).length === 0) return false;
    if (initialSelectedMembers.length === 0) return false;
    const expected = transaction.amount / initialSelectedMembers.length;
    return initialSelectedMembers.some(mid => Math.abs((transaction.splitAmounts[mid] || 0) - expected) > 1);
  })();

  const [splitType, setSplitType] = useState(isCustom ? 'custom' : 'equal');
  const [customAmounts, setCustomAmounts] = useState(transaction.splitAmounts || {});
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const toggleMember = (memberId) => {
    setSelectedMembers(prev =>
      prev.includes(memberId) ? prev.filter(id => id !== memberId) : [...prev, memberId]
    );
  };

  const totalCustom = Object.values(customAmounts).reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const remaining = formData.amount ? parseFloat(formData.amount) - totalCustom : 0;
  const equalSplit = formData.amount && selectedMembers.length > 0 ? parseFloat(formData.amount) / selectedMembers.length : 0;
  const fmt = (n) => n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description || !formData.amount || selectedMembers.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        bill_id: transaction.id,
        description: formData.description,
        amount: parseFloat(formData.amount),
        category: formData.category,
        paid_by_member_id: parseInt(formData.paid_by_member_id),
        split_member_ids: selectedMembers,
      };
      if (splitType === 'custom') {
        const amounts = {};
        selectedMembers.forEach(mid => { amounts[mid] = parseFloat(customAmounts[mid]) || 0; });
        payload.split_amounts = amounts;
      }
      await onSubmit(payload);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-[70] flex items-end sm:items-center justify-center" onClick={onClose}>
        <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-white rounded-t-[32px] sm:rounded-3xl p-6 w-full max-w-md shadow-[0_-10px_40px_rgba(0,0,0,0.1)] max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}>

          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-6 sm:hidden"></div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Edit Tagihan</h3>
            <button onClick={onClose} data-testid="close-edit-bill-modal"
              className="w-8 h-8 rounded-full bg-surface flex items-center justify-center hover:bg-surface-hover transition-colors">
              <XMarkIcon className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Deskripsi</label>
              <input type="text" data-testid="edit-bill-description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" />
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Nominal</label>
              <div className="relative">
                <CurrencyDollarIcon className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
                <input type="text" inputMode="numeric" data-testid="edit-bill-amount" value={formatCurrencyInput(formData.amount)}
                  onChange={(e) => setFormData({ ...formData, amount: parseCurrencyInput(e.target.value) })}
                  className="w-full pl-12 pr-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all" />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Kategori</label>
              <div className="grid grid-cols-3 gap-2">
                {categories.map(cat => {
                  const CatIcon = cat.icon;
                  return (
                    <button key={cat.id} type="button" onClick={() => setFormData({ ...formData, category: cat.id })}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-[16px] border transition-all ${
                        formData.category === cat.id ? 'border-primary bg-primary/5 text-primary' : 'border-border text-slate-600'
                      }`}>
                      <CatIcon className="w-4 h-4" />
                      <span className="text-xs font-medium">{cat.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Dibayar oleh</label>
              <select data-testid="edit-bill-paid-by" value={formData.paid_by_member_id}
                onChange={(e) => setFormData({ ...formData, paid_by_member_id: e.target.value })}
                className="w-full px-4 py-3 rounded-[16px] border border-border focus:outline-none focus:ring-2 focus:ring-primary transition-all bg-white appearance-none">
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Metode Bagi</label>
              <div className="flex gap-2 bg-surface p-1 rounded-full">
                <button type="button" onClick={() => setSplitType('equal')}
                  className={`flex-1 py-2 px-3 rounded-full font-semibold text-sm transition-all ${splitType === 'equal' ? 'bg-primary text-white' : 'text-slate-600'}`}>
                  Bagi Rata
                </button>
                <button type="button" onClick={() => setSplitType('custom')}
                  className={`flex-1 py-2 px-3 rounded-full font-semibold text-sm transition-all flex items-center justify-center gap-1 ${splitType === 'custom' ? 'bg-primary text-white' : 'text-slate-600'}`}>
                  <AdjustmentsHorizontalIcon className="w-4 h-4" /> Custom
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">Dibagi ke</label>
              <div className="flex flex-wrap gap-2">
                {members.map(m => (
                  <button key={m.id} type="button" onClick={() => toggleMember(m.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium transition-all ${
                      selectedMembers.includes(m.id) ? 'border-primary bg-primary text-white' : 'border-border text-slate-600'
                    }`}>
                    <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${m.name}`} alt={m.name} className="w-5 h-5 rounded-full" />
                    {m.name}
                  </button>
                ))}
              </div>
            </div>

            {splitType === 'custom' && formData.amount && (
              <div className="space-y-3">
                {members.filter(m => selectedMembers.includes(m.id)).map(m => (
                  <div key={m.id} className="flex items-center gap-3">
                    <img src={`https://api.dicebear.com/9.x/micah/svg?seed=${m.name}`} alt={m.name} className="w-7 h-7 rounded-full flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700 w-16 truncate">{m.name}</span>
                    <div className="relative flex-1">
                      <span className="text-xs text-slate-400 absolute left-3 top-1/2 -translate-y-1/2">Rp</span>
                      <input type="text" inputMode="numeric" value={formatCurrencyInput(customAmounts[m.id])}
                        onChange={(e) => setCustomAmounts(prev => ({ ...prev, [m.id]: parseCurrencyInput(e.target.value) }))}
                        className="w-full pl-10 pr-3 py-2 rounded-[12px] border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm" />
                    </div>
                  </div>
                ))}
                <div className={`flex items-center justify-between p-3 rounded-[12px] text-sm font-semibold ${Math.abs(remaining) < 1 ? 'bg-success-bg text-success-text' : 'bg-danger-bg text-danger-text'}`}>
                  <span>Sisa:</span><span>Rp {fmt(remaining)}</span>
                </div>
              </div>
            )}

            {splitType === 'equal' && equalSplit > 0 && (
              <div className="bg-surface p-3 rounded-[16px] text-center">
                <p className="text-sm text-slate-600">Per orang: <span className="font-bold text-primary">Rp {fmt(equalSplit)}</span></p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="submit-edit-bill" disabled={loading || !formData.description || !formData.amount || selectedMembers.length === 0 || (splitType === 'custom' && Math.abs(remaining) >= 1)}
                className="flex-1 bg-primary text-white py-3 rounded-full font-semibold hover:bg-primary-hover active:scale-95 transition-all shadow-float disabled:opacity-50">
                {loading ? 'Menyimpan...' : 'Simpan Perubahan'}
              </button>
            </div>

            {/* Delete section */}
            <div className="pt-3 border-t border-border">
              {!showDeleteConfirm ? (
                <button type="button" onClick={() => setShowDeleteConfirm(true)} data-testid="edit-delete-btn"
                  className="w-full flex items-center justify-center gap-2 py-3 text-danger font-semibold hover:bg-danger-bg rounded-full transition-all">
                  Hapus Tagihan Ini
                </button>
              ) : (
                <div className="bg-danger-bg p-4 rounded-2xl">
                  <p className="text-sm text-danger-text mb-3 text-center">Yakin hapus tagihan "{transaction.description}"?</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowDeleteConfirm(false)}
                      className="flex-1 py-2 bg-white text-slate-600 rounded-full font-semibold text-sm">Batal</button>
                    <button type="button" onClick={() => onDelete(transaction.id)} data-testid="confirm-edit-delete-btn"
                      className="flex-1 py-2 bg-danger text-white rounded-full font-semibold text-sm active:scale-95 transition-all">Hapus</button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EditBillModal;
